/**
 * CascadePathRow — one Loop drawn as the path it is running over.
 *
 * WHY THIS EXISTS
 * The catchball surfaces were organised by AUDIENCE: four separate screens
 * (Exchange landing, ObjectiveInbox, ExecutiveInbox, CatchballDashboard) all
 * answering "what is waiting on me", differing only in who was asking. Audience
 * was the only axis left once the product had lost sight of the route.
 *
 * The route is the real spine. A Path is the durable sequence of organizational
 * stops; a Loop runs over exactly one Path; the Ball moves down it one stop at a
 * time and comes back up. Drawing that makes "where is this" answerable at a
 * glance, and makes "is it mine" a filter rather than a separate screen.
 *
 * WHAT IT CAN AND CANNOT SHOW
 * The runtime records the route as org unit ids on the Path Instance
 * (`spineOrgUnitIds`), plus what has actually been traversed. As of 2026-08-28
 * the Loop also carries its Path's identity on `intent.pathName` /
 * `intent.pathSlotId`, so a row can be titled with the name the designer gave the
 * route rather than only drawing its shape. Loops created before that — and any
 * created outside the Designer — have neither, and fall back to the loop number.
 *
 * Stops fall back to the org unit id when the identity store has no name for
 * them; they are never rendered blank.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCatchballPathInstanceStore } from '@/stores/prioritization/catchballPathInstanceStore';
import { useCatchballTierTurnStore } from '@/stores/prioritization/catchballTierTurnStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { readDesignerPathSpine } from '@/lib/catchballPathSpine';
import { buildCatchballInboxUrl } from '@/lib/catchballInboxUrls';
import { status, LABEL_MONO } from '@/components/mono/primitives';
import type { CatchballLoop } from '@/types/prioritization/catchballLoop';

export interface CascadePathRowProps {
  loop: CatchballLoop;
  cycleId: string;
  /** Highlights the stop this viewer is answerable for, if any. */
  viewerOrgUnitIds?: string[];
}

type StopState = 'done' | 'here' | 'ahead';

export function CascadePathRow({ loop, cycleId, viewerOrgUnitIds = [] }: CascadePathRowProps) {
  const fetchPath = useCatchballPathInstanceStore((s) => s.fetchForLoop);
  const pathInstance = useCatchballPathInstanceStore((s) => s.byLoop[loop.id] ?? null);
  const loadTurns = useCatchballTierTurnStore((s) => s.loadLoop);
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);

  useEffect(() => {
    void fetchPath(loop.id);
    void loadTurns(loop.id);
  }, [loop.id, fetchPath, loadTurns]);

  // The Path Instance is materialised the first time a Loop's Catchball Inbox is
  // opened, not at launch — so a Loop nobody has started yet has no instance,
  // and this row used to say "No route recorded for this loop yet." The route
  // WAS recorded: the Designer wrote it onto the Track at launch. Reading it
  // back means an unstarted Loop draws its designed route with every stop
  // ahead, which is the true statement — nobody has walked it — instead of one
  // that reads as missing data.
  const track = useTrackStore((s) => (loop.trackId ? s.getTrack(loop.trackId) : undefined));
  const designedSpine = useMemo(
    () => readDesignerPathSpine((track?.metadata ?? null) as Record<string, unknown> | null),
    [track],
  );

  const stops = useMemo(() => {
    const spine = pathInstance?.spineOrgUnitIds?.length
      ? pathInstance.spineOrgUnitIds
      : designedSpine;
    const walked = new Set((pathInstance?.traversed ?? []).map((t) => t.org_unit_id));
    // The current stop is the last one walked; everything after it is still ahead.
    const lastWalked = [...spine].reverse().find((id) => walked.has(id)) ?? null;
    return spine.map((id) => {
      const state: StopState =
        id === lastWalked ? 'here' : walked.has(id) ? 'done' : 'ahead';
      return {
        id,
        // Never render a blank stop: fall back to the id if identity has no name.
        label: orgUnits.get(id)?.displayName ?? orgUnits.get(id)?.name ?? id,
        state,
        isYours: viewerOrgUnitIds.includes(id),
      };
    });
  }, [pathInstance, designedSpine, orgUnits, viewerOrgUnitIds]);

  const hereStop = stops.find((s) => s.state === 'here');
  const yoursNow = Boolean(hereStop?.isYours);

  return (
    <div
      className="border bg-[var(--bg-raised)] p-4"
      style={{ borderColor: yoursNow ? 'var(--mustard)' : 'var(--rule)' }}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Named by the Path the Designer drew, when the Loop carries it. */}
          <span className={LABEL_MONO}>
            {loop.intent?.pathName
              ? `${loop.intent.pathName} · Loop ${loop.loopNumber}`
              : `Loop ${loop.loopNumber}`}
          </span>
          {yoursNow ? (
            <span {...status('mustard')}>In your court</span>
          ) : hereStop ? (
            <span {...status('slate')}>With {hereStop.label}</span>
          ) : (
            <span {...status('neutral')}>Not yet moving</span>
          )}
        </div>
        <Link
          to={buildCatchballInboxUrl(cycleId, loop.id)}
          className="whitespace-nowrap font-mono text-[0.72rem] uppercase tracking-[0.08em] text-[var(--text-ter)] hover:text-[var(--mustard)]"
        >
          Open →
        </Link>
      </div>

      {stops.length === 0 ? (
        <p className="text-sm text-[var(--text-dim)]">
          No route recorded for this loop yet.
        </p>
      ) : (
        <ol className="flex flex-wrap items-center gap-x-1 gap-y-2">
          {stops.map((stop, i) => (
            <li key={stop.id} className="flex items-center gap-1">
              {i > 0 && <span className="px-1 text-[var(--text-dim)]">→</span>}
              <span
                className="border px-2.5 py-1 font-mono text-[0.72rem] uppercase tracking-[0.08em]"
                style={{
                  borderColor:
                    stop.state === 'here' ? 'var(--mustard)'
                    : stop.state === 'done' ? 'var(--green)'
                    : 'var(--rule)',
                  color:
                    stop.state === 'here' ? 'var(--mustard)'
                    : stop.state === 'done' ? 'var(--green)'
                    : 'var(--text-dim)',
                }}
                title={
                  stop.state === 'here' ? 'The ball is here'
                  : stop.state === 'done' ? 'Already answered'
                  : 'Not yet reached'
                }
              >
                {stop.label}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default CascadePathRow;
